import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import { CategoryIcon, FolderIcon, FileIcon, MiscIcon } from '../components/LibIcon';
import { getLibrary, getLibraryCategory, getLibraryFolders, getLibraryItem, searchLibrary } from '../api/client';

const TYPE_LABELS = { video: 'Видео', pdf: 'PDF документ', xlsx: 'Excel таблица', doc: 'Word документ', link: 'Ссылка', qa: 'Вопрос и ответ' };

export default function LibraryPage() {
  const [screen, setScreen] = useState('main'); // main | category | folder | detail
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [activeFolder, setActiveFolder] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [lbZoom, setLbZoom] = useState(1);
  const lbStartDist = useRef(null);
  const lbSwipeX = useRef(null);

  const openLightbox = (images, index) => { setLightbox({ images, index }); setLbZoom(1); };
  const lbPrev = () => { if (lightbox && lightbox.index > 0) { setLightbox({ ...lightbox, index: lightbox.index - 1 }); setLbZoom(1); } };
  const lbNext = () => { if (lightbox && lightbox.index < lightbox.images.length - 1) { setLightbox({ ...lightbox, index: lightbox.index + 1 }); setLbZoom(1); } };

  const loadMain = useCallback(() => {
    getLibrary().then(data => {
      setCategories(data.categories || []);
      setRecent(data.recent || []);
    }).catch(console.error);
  }, []);

  useEffect(() => { loadMain(); }, [loadMain]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden && screen === 'main') loadMain(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadMain, screen]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchLibrary(search).then(data => { setSearchResults(data.items || []); setSearching(false); }).catch(() => { setSearchResults([]); setSearching(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openCategory = async (cat) => {
    setActiveCategory(cat);
    try {
      const data = await getLibraryFolders(cat.slug);
      const flds = data.folders || [];
      const unfiled = data.unfiled_count || 0;
      setFolders(flds);
      setUnfiledCount(unfiled);
      if (flds.length === 0 && unfiled > 0) {
        setActiveFolder({ name: cat.name, slug: cat.slug });
        const itemsData = await getLibraryCategory(cat.slug);
        setItems(itemsData.items || []);
        setScreen('folder');
        return;
      }
    } catch (e) {
      setFolders([]);
      setUnfiledCount(0);
    }
    setScreen('category');
  };

  const openFolder = async (folder) => {
    setActiveFolder(folder);
    const data = await getLibraryCategory(activeCategory.slug, `folder_id=${folder.id}`);
    setItems(data.items || []);
    setScreen('folder');
  };

  const openUnfiled = async () => {
    setActiveFolder({ name: 'Прочее', misc: 'list' });
    const data = await getLibraryCategory(activeCategory.slug, 'folder_id=none');
    setItems(data.items || []);
    setScreen('folder');
  };

  const goBack = () => {
    if (screen === 'detail') {
      setScreen('folder');
      setActiveItem(null);
    } else if (screen === 'folder') {
      if (folders.length === 0) {
        setScreen('main');
        setActiveCategory(null);
        setActiveFolder(null);
        setItems([]);
      } else {
        setScreen('category');
        setActiveFolder(null);
        setItems([]);
      }
    } else {
      setScreen('main');
      setActiveCategory(null);
      setFolders([]);
    }
  };

  const openItem = async (item) => {
    if (item.description || item.speaker || item.file_type === 'qa') {
      try {
        const full = await getLibraryItem(item.id);
        setActiveItem(full);
      } catch {
        setActiveItem(item);
      }
      setScreen('detail');
    } else if (item.file_url) {
      getLibraryItem(item.id).catch(() => {});
      openLink(item.file_url);
    }
  };

  const openLink = (url) => {
    const fullUrl = url.startsWith('http') ? url : window.location.origin + url;
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(fullUrl);
    } else {
      window.open(fullUrl, '_blank');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <Header title="Библиотека" />
      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Search */}
        {screen !== 'detail' && (
          <div style={{ position: 'relative', animation: 'fadeInUp 0.3s ease both' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', opacity: 0.5, display: 'flex' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск: договор, смета, налоги..."
              style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px 12px 40px', fontSize: 15, color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        )}

        {/* === SEARCH RESULTS === */}
        {searching && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'center', padding: 20 }}>Поиск...</div>}
        {!searching && searchResults !== null && <>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)' }}>
            {searchResults.length > 0 ? `Найдено: ${searchResults.length}` : 'Ничего не найдено'}
          </div>
          {searchResults.map((item, i) => (
            <div key={item.id} onClick={() => openItem(item)} style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14,
              cursor: 'pointer', animation: `fadeInUp 0.2s ease ${i * 0.03}s both`,
            }}>
              <div style={{
                width: 46, height: 46, borderRadius: 12,
                background: 'var(--accent-dim)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><FileIcon type={item.file_type} size={24} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, lineHeight: 1.3 }}>{item.title}</div>
                {item.speaker && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5 }}><MiscIcon name="mic" size={12} />{item.speaker}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 8 }}>
                  {item.category_name && <span>{item.category_name}</span>}
                  {item.duration && <span>{item.duration}</span>}
                </div>
              </div>
              <span style={{ flexShrink: 0, color: 'var(--text-tertiary)' }}><MiscIcon name="chevron" size={18} strokeWidth={2} /></span>
            </div>
          ))}
        </>}

        {/* === MAIN: Categories Grid === */}
        {screen === 'main' && searchResults === null && <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeInUp 0.3s ease 0.05s both' }}>
            {categories.map(cat => (
              <div key={cat.slug} onClick={() => openCategory(cat)} style={{
                background: 'var(--bg-card)', borderRadius: 16, padding: '16px 14px',
                border: '1px solid var(--border)', cursor: 'pointer', position: 'relative',
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, marginBottom: 12,
                  background: 'var(--accent-dim)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><CategoryIcon slug={cat.slug} size={28} /></div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{cat.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{cat.count || 0} шт</div>
                <span style={{ position: 'absolute', top: 16, right: 12, color: 'var(--text-tertiary)' }}><MiscIcon name="chevron" size={16} strokeWidth={2} /></span>
              </div>
            ))}
          </div>

          {recent.length > 0 && (
            <div style={{ animation: 'fadeInUp 0.3s ease 0.15s both' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Новое в библиотеке</div>
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
                {recent.map(item => (
                  <div key={item.id} onClick={() => openItem(item)} style={{
                    background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)',
                    flexShrink: 0, width: 200, overflow: 'hidden', cursor: 'pointer',
                  }}>
                    <div style={{
                      height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: item.file_type === 'video' ? 'linear-gradient(135deg, #1a1a2e, #16213e)' : 'linear-gradient(135deg, #1e1e1e, #2a2a1e)',
                      color: 'var(--accent)',
                    }}><FileIcon type={item.file_type} size={32} strokeWidth={1.6} /></div>
                    <div style={{ padding: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{item.title}</div>
                      {item.speaker && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}><MiscIcon name="mic" size={11} />{item.speaker}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{item.duration || (item.meta && !item.meta.startsWith('event_') ? item.meta : item.file_type)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>}

        {/* === CATEGORY: Folders Grid === */}
        {screen === 'category' && activeCategory && searchResults === null && <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 15, fontWeight: 500, cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', textAlign: 'left', animation: 'fadeInUp 0.3s ease both' }}>
            ← Библиотека
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, animation: 'fadeInUp 0.3s ease 0.05s both' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'var(--accent-dim)', color: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><CategoryIcon slug={activeCategory.slug} size={30} /></div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{activeCategory.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {(folders.reduce((s, f) => s + (f.count || 0), 0) + unfiledCount)} материалов
              </div>
            </div>
          </div>

          {folders.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeInUp 0.3s ease 0.1s both' }}>
              {folders.map(folder => (
                <div key={folder.id} onClick={() => openFolder(folder)} style={{
                  background: 'var(--bg-card)', borderRadius: 14, padding: '16px 14px',
                  border: '1px solid var(--border)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: 'var(--accent-dim)', color: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><FolderIcon name={folder.name} size={22} /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{folder.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{folder.count || 0} шт</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {unfiledCount > 0 && (
            <div onClick={openUnfiled} style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
              animation: 'fadeInUp 0.3s ease 0.15s both',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><MiscIcon name="list" size={22} strokeWidth={1.8} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Прочее</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{unfiledCount} шт</div>
              </div>
              <span style={{ color: 'var(--text-tertiary)' }}><MiscIcon name="chevron" size={16} strokeWidth={2} /></span>
            </div>
          )}

          {folders.length === 0 && unfiledCount === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>Пока пусто</div>
          )}
        </>}

        {/* === FOLDER: Files List === */}
        {screen === 'folder' && activeFolder && searchResults === null && <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 15, fontWeight: 500, cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', textAlign: 'left', animation: 'fadeInUp 0.3s ease both' }}>
            ← {activeCategory.name}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, animation: 'fadeInUp 0.3s ease 0.05s both' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, flexShrink: 0,
              background: 'var(--accent-dim)', color: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {activeFolder.slug
                ? <CategoryIcon slug={activeFolder.slug} size={30} />
                : activeFolder.misc
                  ? <MiscIcon name={activeFolder.misc} size={28} strokeWidth={1.8} />
                  : <FolderIcon name={activeFolder.name} size={30} />}
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{activeFolder.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{items.length} {items.length === 1 ? 'материал' : 'материалов'}</div>
            </div>
          </div>

          {items.map((item, i) => (
            <div key={item.id} onClick={() => openItem(item)} style={{
              background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
              border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14,
              cursor: 'pointer', animation: `fadeInUp 0.3s ease ${0.1 + i * 0.04}s both`,
            }}>
              <div style={{
                width: 46, height: 46, borderRadius: 12,
                background: 'var(--accent-dim)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><FileIcon type={item.file_type} size={24} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, lineHeight: 1.3 }}>{item.title}</div>
                {item.speaker && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5 }}><MiscIcon name="mic" size={12} />{item.speaker}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 8 }}>
                  {item.duration && <span>{item.duration}</span>}
                  {item.lessons_count && <span>{item.lessons_count} уроков</span>}
                  {item.meta && !item.meta.startsWith('event_') && <span>{item.meta}</span>}
                  {!item.duration && !item.lessons_count && !item.meta && <span>{item.file_type?.toUpperCase()}</span>}
                </div>
              </div>
              <span style={{ flexShrink: 0, color: 'var(--text-tertiary)' }}><MiscIcon name="chevron" size={18} strokeWidth={2} /></span>
            </div>
          ))}

          {items.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>Пока пусто</div>
          )}
        </>}

        {/* === DETAIL: Item Detail View === */}
        {screen === 'detail' && activeItem && <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 15, fontWeight: 500, cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', textAlign: 'left', animation: 'fadeInUp 0.2s ease both' }}>
            ← Назад
          </button>

          {activeItem.file_type === 'qa' && activeItem.question ? (
            /* ===== Q&A DETAIL VIEW ===== */
            <div style={{ animation: 'fadeInUp 0.3s ease both' }}>
              {/* Question card */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 16, border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                    ❓ Вопрос
                  </span>
                  {activeItem.question.category && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'rgba(99,99,102,0.12)', color: 'var(--text-secondary)' }}>
                      {activeItem.question.category}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{activeItem.question.text}</div>
                {/* Question photos */}
                {activeItem.question.photos && activeItem.question.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {activeItem.question.photos.map((p, i) => {
                      const filePath = typeof p === 'string' ? p : p.file_path;
                      const fileName = typeof p === 'string' ? filePath.split('/').pop() : (p.file_name || filePath.split('/').pop());
                      const isImage = /\.(jpg|jpeg|png|webp)$/i.test(filePath);
                      return isImage
                        ? <img key={i} src={filePath} alt="" onClick={() => { const allImgs = activeItem.question.photos.filter(x => /\.(jpg|jpeg|png|webp)$/i.test(typeof x === 'string' ? x : x.file_path)).map(x => typeof x === 'string' ? x : x.file_path); openLightbox(allImgs, allImgs.indexOf(filePath)); }} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer' }} />
                        : <a key={i} href={filePath} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>📎 {fileName}</a>;
                    })}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 10, display: 'flex', gap: 8 }}>
                  <span>👤 {activeItem.question.author_name}</span>
                  <span>📅 {formatDate(activeItem.question.created_at)}</span>
                </div>
              </div>

              {/* Answers */}
              {activeItem.question.answers && activeItem.question.answers.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                    Ответы ({activeItem.question.answers.length})
                  </div>
                  {activeItem.question.answers.map((a, i) => {
                    const isExp = a.author_role === 'admin' || a.author_role === 'expert';
                    return (
                      <div key={i} style={{
                        background: 'var(--bg-card)', borderRadius: 12, padding: '12px 14px',
                        border: '1px solid var(--border)', marginBottom: 8,
                        borderLeft: isExp ? '3px solid #30D158' : '3px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{a.author_name}</span>
                          {isExp && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(48,209,88,0.12)', color: '#30D158' }}>Эксперт</span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.text}</div>
                        {/* Answer attachments */}
                        {a.attachments && a.attachments.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            {a.attachments.map((att, j) => {
                              const isImg = /\.(jpg|jpeg|png|webp)$/i.test(att.file_path);
                              return isImg
                                ? <img key={j} src={att.file_path} alt="" onClick={() => { const allImgs = a.attachments.filter(x => /\.(jpg|jpeg|png|webp)$/i.test(x.file_path)).map(x => x.file_path); openLightbox(allImgs, allImgs.indexOf(att.file_path)); }} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                                : <a key={j} href={att.file_path} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none' }}>📎 {att.file_name || 'Файл'}</a>;
                            })}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                          {formatDate(a.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Meta */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {activeItem.views > 0 && (
                  <span style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: 13, color: 'var(--text-tertiary)' }}>
                    👁 {activeItem.views}
                  </span>
                )}
                {activeItem.speaker && (
                  <span style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: 13, color: 'var(--accent)' }}>
                    🧑‍💼 {activeItem.speaker}
                  </span>
                )}
              </div>
            </div>
          ) : (
            /* ===== REGULAR DETAIL VIEW ===== */
            <>
              {/* Hero */}
              <div style={{
                background: activeItem.file_type === 'video' || activeItem.file_type === 'link'
                  ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                  : 'linear-gradient(135deg, #1e1e1e 0%, #2a2a1e 100%)',
                borderRadius: 18, padding: '32px 20px', textAlign: 'center',
                border: '1px solid var(--border)', position: 'relative', overflow: 'hidden',
                animation: 'fadeInUp 0.3s ease both',
              }}>
                <div style={{
                  width: 76, height: 76, borderRadius: 20, margin: '0 auto 14px',
                  background: 'var(--accent-dim)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><FileIcon type={activeItem.file_type} size={40} strokeWidth={1.6} /></div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: 'var(--text-tertiary)', marginBottom: 8 }}>
                  {TYPE_LABELS[activeItem.file_type] || activeItem.file_type?.toUpperCase()}
                </div>
              </div>

              {/* Title & Speaker */}
              <div style={{ animation: 'fadeInUp 0.3s ease 0.05s both' }}>
                <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>{activeItem.title}</div>
                {activeItem.speaker && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'var(--accent-dim)', borderRadius: 10, padding: '6px 12px', marginTop: 4,
                  }}>
                    <span style={{ color: 'var(--accent)', display: 'flex' }}><MiscIcon name="mic" size={15} /></span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>{activeItem.speaker}</span>
                  </div>
                )}
              </div>

              {/* Meta chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, animation: 'fadeInUp 0.3s ease 0.1s both' }}>
                {activeItem.duration && (
                  <span style={chipStyle}>
                    <MiscIcon name="clock" size={13} />{activeItem.duration}
                  </span>
                )}
                {activeItem.lessons_count > 0 && (
                  <span style={chipStyle}>
                    <MiscIcon name="book" size={13} />{activeItem.lessons_count} уроков
                  </span>
                )}
                {activeItem.meta && !activeItem.meta.startsWith('event_') && (
                  <span style={chipStyle}>
                    <MiscIcon name="tag" size={13} />{activeItem.meta}
                  </span>
                )}
                {activeItem.views > 0 && (
                  <span style={{ ...chipStyle, color: 'var(--text-tertiary)' }}>
                    <MiscIcon name="eye" size={13} />{activeItem.views}
                  </span>
                )}
                {activeItem.created_at && (
                  <span style={{ ...chipStyle, color: 'var(--text-tertiary)' }}>
                    <MiscIcon name="calendar" size={13} />{formatDate(activeItem.created_at)}
                  </span>
                )}
              </div>

              {/* Description */}
              {activeItem.description && (
                <div style={{
                  background: 'var(--bg-card)', borderRadius: 16, padding: '16px 18px',
                  border: '1px solid var(--border)',
                  animation: 'fadeInUp 0.3s ease 0.15s both',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>О материале</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-line' }}>
                    {activeItem.description}
                  </div>
                </div>
              )}

              {/* Open button */}
              {activeItem.file_url && (
                <button onClick={() => openLink(activeItem.file_url)} style={{
                  width: '100%', padding: '16px 20px',
                  background: 'var(--accent)', color: '#000', border: 'none',
                  borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  animation: 'fadeInUp 0.3s ease 0.2s both',
                }}>
                  <MiscIcon name={activeItem.file_type === 'video' || activeItem.file_type === 'link' ? 'play' : 'download'} size={18} strokeWidth={2.2} />
                  {activeItem.file_type === 'video' || activeItem.file_type === 'link' ? 'Смотреть' : 'Скачать'}
                  <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.7 }}>
                    {TYPE_LABELS[activeItem.file_type] || ''}
                  </span>
                </button>
              )}
            </>
          )}
        </>}
      </div>

      {/* Lightbox gallery */}
      {lightbox && (
        <div onClick={() => { setLightbox(null); setLbZoom(1); }}
          onTouchStart={e => {
            if (e.touches.length === 1) { lbSwipeX.current = e.touches[0].clientX; }
            if (e.touches.length === 2) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; lbStartDist.current = Math.hypot(dx, dy); lbSwipeX.current = null; }
          }}
          onTouchMove={e => {
            if (e.touches.length === 2 && lbStartDist.current) { const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; const dist = Math.hypot(dx, dy); const scale = dist / lbStartDist.current; setLbZoom(z => Math.min(5, Math.max(0.5, z * (scale > 1 ? 1.02 : 0.98)))); lbStartDist.current = dist; e.preventDefault(); }
          }}
          onTouchEnd={e => {
            if (lbSwipeX.current !== null && e.changedTouches.length === 1 && lbZoom <= 1) {
              const diff = e.changedTouches[0].clientX - lbSwipeX.current;
              if (diff > 60) lbPrev();
              else if (diff < -60) lbNext();
            }
            lbSwipeX.current = null; lbStartDist.current = null;
          }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, cursor: 'pointer', touchAction: 'none',
          }}>
          <button onClick={e => { e.stopPropagation(); setLightbox(null); setLbZoom(1); }} style={{
            position: 'absolute', top: 16, right: 16, background: 'rgba(60,60,60,0.8)',
            border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18,
            color: '#FFCC00', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 2, fontWeight: 700,
          }}>✕</button>
          {lightbox.images.length > 1 && (
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#FFCC00', fontSize: 14, fontWeight: 700 }}>
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
          {lightbox.images.length > 1 && lightbox.index > 0 && (
            <button onClick={e => { e.stopPropagation(); lbPrev(); }} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, fontSize: 22, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, fontWeight: 700,
            }}>‹</button>
          )}
          {lightbox.images.length > 1 && lightbox.index < lightbox.images.length - 1 && (
            <button onClick={e => { e.stopPropagation(); lbNext(); }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(60,60,60,0.8)', border: 'none', borderRadius: '50%',
              width: 40, height: 40, fontSize: 22, color: '#FFCC00', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, fontWeight: 700,
            }}>›</button>
          )}
          <div onClick={e => e.stopPropagation()} style={{ overflow: 'auto', maxWidth: '100%', maxHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={lightbox.images[lightbox.index]} alt="" style={{
              transform: `scale(${lbZoom})`, transformOrigin: 'center center',
              maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8,
              transition: 'transform 0.1s ease',
            }} />
          </div>
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 24, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setLbZoom(z => Math.max(0.5, z - 0.3))} style={lbBtnStyle}>−</button>
            <button onClick={() => setLbZoom(1)} style={{ ...lbBtnStyle, fontSize: 12, minWidth: 44 }}>{Math.round(lbZoom * 100)}%</button>
            <button onClick={() => setLbZoom(z => Math.min(5, z + 0.3))} style={lbBtnStyle}>+</button>
            <button onClick={() => { const src = lightbox.images[lightbox.index]; const url = new URL(src, window.location.origin).href; const fileName = src.split('/').pop() || 'photo.jpg'; if (window.Telegram?.WebApp?.downloadFile) { window.Telegram.WebApp.downloadFile({ url, file_name: fileName }); } else { window.open(url, '_blank'); } }} style={{ ...lbBtnStyle, padding: '10px 20px' }}>Скачать</button>
          </div>
        </div>
      )}
    </div>
  );
}

const lbBtnStyle = {
  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
  padding: '10px 14px', fontSize: 18, color: '#fff', fontWeight: 600, cursor: 'pointer',
};

const chipStyle = {
  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '5px 10px', fontSize: 13, color: 'var(--text-secondary)',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
